import { useCallback, useEffect, useState } from "react";
import { AuthContext } from "./auth.context";
import { ErrorStateObject, GetLoginCodeRequest, LoadingStateObject, LoginRequest, TokenRequest } from "../../types/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthLoginMutation } from "../../api/auth";
import { useCurrentUser } from "../../api/users";
import { clearAuthTokens, getAuthTokens, getTenantId, removeTenantId, saveTenantId, setAuthTokens } from "../../api/utils";
import { useSnackbar } from 'notistack';
import { ERROR_SNACKBAR } from "../../consts";
import { CurrentUserResponse } from "../../types/users";

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [isSecure, setIsSecure] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<CurrentUserResponse | undefined>(undefined);
  const { loading: loginLoading, error: loginError, getCodeMutation, loginMutation, tokenMutation } = useAuthLoginMutation();
  const { query: queryCurrentUser, loading: currentUserLoading, error: currentUserError } = useCurrentUser();

  const loadingState: LoadingStateObject = {
    login: loginLoading,
    currentUser: currentUserLoading,
  };
  const errorState: ErrorStateObject = {
    login: loginError,
    currentUser: currentUserError,
  };

  const getLoginCode = useCallback(
    async (data: GetLoginCodeRequest) => {
      return await getCodeMutation(data);
    },
    [getCodeMutation]
  );

  const getTenants = useCallback(
    async (loginData: LoginRequest) => {
      const { data, success } = await loginMutation(loginData);
      return { data, success };
    },
    [loginMutation]
  );

  const refreshCurrentUser = useCallback(async () => {
    if (isSecure) {
      const data = await queryCurrentUser();
      setCurrentUser(data);
    }
  }, [isSecure, queryCurrentUser]);

  const setTenant = useCallback(
    async (requestData: TokenRequest, tenantid: string) => {
      saveTenantId(tenantid);
      const { data, success } = await tokenMutation(requestData);
      if (data && success) {
        setAuthTokens({ access: data.access, refresh: data.refresh })
        setIsSecure(true);
        // Fetch current user after setting tenant
        const userData = await queryCurrentUser();
        setCurrentUser(userData);
      };
    },
    [tokenMutation, queryCurrentUser]
  );

  const logout = useCallback(
    () => {
      clearAuthTokens();
      removeTenantId();
      setIsSecure(false);
      setCurrentUser(undefined);
    },
    []
  );

  useEffect(() => {
    if (loginError && loginError.message) enqueueSnackbar(loginError.message, ERROR_SNACKBAR);
  }, [loginError, enqueueSnackbar]);

  useEffect(() => {
    const tokens = getAuthTokens();
    const isSecureCheck = !!tokens?.access && !!tokens?.refresh && !!getTenantId();
    setIsSecure(isSecureCheck);
    if (!isSecureCheck) logout();
  }, [location, logout, currentUser, currentUserLoading]);

  useEffect(() => {
    if (isSecure && location.pathname === '/login') navigate('/');
  }, [isSecure, location, navigate]);

  useEffect(() => {
    if (!currentUser && !currentUserLoading) {
      refreshCurrentUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to profile and tenant update events to refresh current user
  useEffect(() => {
    const handleCurrentUserUpdated = () => {
      refreshCurrentUser();
    };

    window.addEventListener('profileUpdated', handleCurrentUserUpdated);
    window.addEventListener('tenantUpdated', handleCurrentUserUpdated);

    return () => {
      window.removeEventListener('profileUpdated', handleCurrentUserUpdated);
      window.removeEventListener('tenantUpdated', handleCurrentUserUpdated);
    };
  }, [refreshCurrentUser]);

  return (
    <AuthContext.Provider value={{
      getLoginCode,
      login: getTenants,
      setTenant,
      logout,
      isSecure,
      loadingState,
      errorState,
      currentUser,
      setCurrentUser,
      currentUserLoading,
      currentUserError,
      refreshCurrentUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
